import { hasFeature } from "./product";

export const ACTION_ITEM_PRIORITY_OPTIONS = [
  "Extremely High",
  "High",
  "Mid",
  "Low",
  "Extremely Low",
];

export const ACTION_ITEM_STATUS_OPTIONS = [
  "open",
  "in_progress",
  "completed",
  "closed_irrelevant",
  "closed_outdated",
  "closed",
  "cancelled",
];

export const ACTION_ITEM_TERMINAL_STATUSES = [
  "completed",
  "closed_irrelevant",
  "closed_outdated",
  "closed",
  "cancelled",
];

export const ACTION_ITEM_STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  completed: "Completed",
  closed_irrelevant: "Closed - Irrelevant",
  closed_outdated: "Closed - Out of Date",
  closed: "Closed",
  cancelled: "Cancelled",
};

export const ACTION_ITEM_CLOSURE_CATEGORY_OPTIONS = [
  "completed",
  "irrelevant",
  "outdated_incomplete",
  "closed_incomplete",
];

const ACTION_ITEM_STATUS_ALIASES = {
  active: "open",
  pending: "open",
  complete: "completed",
  done: "completed",
  closed_not_relevant: "closed_irrelevant",
  irrelevant: "closed_irrelevant",
  not_relevant: "closed_irrelevant",
  not_in_scope: "closed_irrelevant",
  out_of_scope: "closed_irrelevant",
  outdated: "closed_outdated",
  out_of_date: "closed_outdated",
  "out of date": "closed_outdated",
  canceled: "cancelled",
};

const ACTION_ITEM_DEFAULT_CLOSURE_CATEGORY_BY_STATUS = {
  completed: "completed",
  closed_irrelevant: "irrelevant",
  closed_outdated: "outdated_incomplete",
  closed: "closed_incomplete",
  cancelled: "closed_incomplete",
};

export const ACTION_ITEM_MINUTES_MIN = 1;
export const ACTION_ITEM_MINUTES_MAX = 2880;
export const ACTION_ITEM_SUMMARY_WINDOW_DAYS = 7;

const ACTION_ITEM_PRIORITY_WEIGHT = {
  "Extremely High": 5,
  High: 4,
  Mid: 3,
  Low: 2,
  "Extremely Low": 1,
};

const hasValue = (value) =>
  value !== undefined && value !== null && `${value}`.trim() !== "";

export const isWithinActionItemSummaryWindow = (item = {}, referenceDate = new Date()) => {
  const dueDateValue =
    item?.suggestedDueDate ?? item?.suggested_due_date ?? item?.dueDate ??
    item?.due_date ?? item?.date ?? null;
  if (!dueDateValue) return true;

  const dueDate = new Date(dueDateValue);
  if (Number.isNaN(dueDate.getTime()) || !(referenceDate instanceof Date) || Number.isNaN(referenceDate.getTime())) {
    return false;
  }

  const windowStart = new Date(Date.UTC(
    referenceDate.getUTCFullYear(), referenceDate.getUTCMonth(),
    referenceDate.getUTCDate() - ACTION_ITEM_SUMMARY_WINDOW_DAYS,
  ));
  const windowEnd = new Date(Date.UTC(
    referenceDate.getUTCFullYear(), referenceDate.getUTCMonth(),
    referenceDate.getUTCDate() + ACTION_ITEM_SUMMARY_WINDOW_DAYS + 1,
  ));

  return dueDate >= windowStart && dueDate < windowEnd;
};

const coerceString = (value) => {
  if (!hasValue(value)) {
    return "";
  }

  return typeof value === "string" ? value.trim() : String(value).trim();
};

const coerceNullableString = (value) => {
  if (!hasValue(value)) {
    return null;
  }

  return typeof value === "string" ? value.trim() : String(value).trim();
};

const isEmailLike = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(coerceString(value));

const CASE_LABEL_UPPERCASE_TOKENS = new Set([
  "llc",
  "llp",
  "lp",
  "ltd",
  "inc",
  "corp",
  "co",
  "pllc",
  "pc",
  "na",
  "usa",
]);

const decodeDisplayString = (value) => {
  const normalized = coerceString(value);

  if (!normalized) {
    return "";
  }

  try {
    return decodeURIComponent(normalized);
  } catch (error) {
    void error;
    return normalized;
  }
};

const titleCaseWord = (value) =>
  value
    .split("'")
    .map((segment) =>
      segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}` : "",
    )
    .join("'");

const humanizeCaseCaption = (value) => {
  const decoded = decodeDisplayString(value);

  if (!decoded) {
    return "";
  }

  return decoded
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const normalizedWord = word.toLowerCase();

      if (normalizedWord === "v" || normalizedWord === "vs" || normalizedWord === "vs.") {
        return "v.";
      }

      if (CASE_LABEL_UPPERCASE_TOKENS.has(normalizedWord)) {
        return normalizedWord.toUpperCase();
      }

      return titleCaseWord(word);
    })
    .join(" ");
};

const normalizeCaseLabel = (value) => {
  const decoded = decodeDisplayString(value);

  if (!decoded) {
    return "";
  }

  if (/[-_]/.test(decoded) || !/[A-Z]/.test(decoded)) {
    return humanizeCaseCaption(decoded);
  }

  return decoded.replace(/\s+/g, " ").trim();
};

export const normalizeStringSet = (value) => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(value.map((entry) => coerceNullableString(entry)).filter(Boolean)),
    );
  }

  const normalizedValue = coerceNullableString(value);

  if (!normalizedValue) {
    return [];
  }

  try {
    const parsed = JSON.parse(normalizedValue);
    if (Array.isArray(parsed)) {
      return Array.from(
        new Set(parsed.map((entry) => coerceNullableString(entry)).filter(Boolean)),
      );
    }
  } catch (error) {
    void error;
  }

  if (normalizedValue.includes(",")) {
    return Array.from(
      new Set(
        normalizedValue
          .split(",")
          .map((entry) => coerceNullableString(entry))
          .filter(Boolean),
      ),
    );
  }

  return [normalizedValue];
};

// Shown for a person id that resolves to nobody on the roster (a member who
// left, or an id from another id space). Never render the raw id instead.
export const ACTION_ITEM_UNKNOWN_PERSON_LABEL = "Former member";

const PERSON_ID_EMPTY_TOKENS = new Set(["", "none", "null", "undefined", "set()", "[]", "{}"]);
const PERSON_ID_MAX_DEPTH = 8;

const unquotePersonToken = (text) => {
  if (text.length >= 2) {
    const first = text[0];
    if ((first === "'" || first === '"') && text[text.length - 1] === first) {
      return text.slice(1, -1).trim();
    }
  }
  return text;
};

// Splits on commas that sit outside quotes and brackets, so
// `'a', "{'b'}"` yields two tokens.
const splitTopLevelPersonTokens = (text) => {
  const tokens = [];
  let depth = 0;
  let quote = null;
  let current = "";

  for (const character of text) {
    if (quote) {
      if (character === quote) {
        quote = null;
      }
      current += character;
    } else if (character === "'" || character === '"') {
      quote = character;
      current += character;
    } else if (character === "[" || character === "{" || character === "(") {
      depth += 1;
      current += character;
    } else if (character === "]" || character === "}" || character === ")") {
      depth = Math.max(0, depth - 1);
      current += character;
    } else if (character === "," && depth === 0) {
      tokens.push(current);
      current = "";
    } else {
      current += character;
    }
  }

  tokens.push(current);
  return tokens.map((token) => token.trim()).filter(Boolean);
};

const parsePersonIdentifierTokens = (value, depth = 0) => {
  if (depth > PERSON_ID_MAX_DEPTH || !hasValue(value)) {
    return [];
  }

  if (Array.isArray(value) || value instanceof Set) {
    return Array.from(value).flatMap((entry) => parsePersonIdentifierTokens(entry, depth + 1));
  }

  if (typeof value === "object") {
    return parsePersonIdentifierTokens(
      value.userId ?? value.user_id ?? value.id ?? value.email ?? null,
      depth + 1,
    );
  }

  const text = coerceString(value);
  if (PERSON_ID_EMPTY_TOKENS.has(text.toLowerCase())) {
    return [];
  }

  const unquoted = unquotePersonToken(text);
  if (unquoted !== text) {
    return parsePersonIdentifierTokens(unquoted, depth + 1);
  }

  const first = text[0];
  const last = text[text.length - 1];
  if (first === "[" || first === "{") {
    try {
      const parsed = JSON.parse(text);
      if (parsed !== text) {
        return parsePersonIdentifierTokens(parsed, depth + 1);
      }
    } catch (error) {
      void error;
    }
  }

  // Python reprs persisted by backend writers: {'id'}, ['id'], set(['id']).
  if ((first === "[" && last === "]") || (first === "{" && last === "}")) {
    return splitTopLevelPersonTokens(text.slice(1, -1)).flatMap((token) =>
      parsePersonIdentifierTokens(token, depth + 1),
    );
  }

  if (/^set\(.*\)$/i.test(text)) {
    return parsePersonIdentifierTokens(text.slice(4, -1), depth + 1);
  }

  if (text.includes(",")) {
    const tokens = splitTopLevelPersonTokens(text);
    if (tokens.length > 1) {
      return tokens.flatMap((token) => parsePersonIdentifierTokens(token, depth + 1));
    }
  }

  return [text];
};

// Assignee/reviewer ids arrive as arrays, JSON strings, comma lists and, from
// some backend writers, Python set/list reprs (even nested ones such as
// `{"{'uuid'}"}`). Returns clean, de-duplicated ids.
export const parseActionItemPersonIds = (value) =>
  Array.from(new Set(parsePersonIdentifierTokens(value)));

export const normalizeActionItemPersonId = (value) =>
  parseActionItemPersonIds(value)[0] ?? null;

export const getActionItemAssignedToValues = (item = {}) =>
  parseActionItemPersonIds(
    item?.assignedToValues ??
      item?.assigned_to_values ??
      item?.assigneeValues ??
      item?.assignee_values ??
      item?.userIds ??
      item?.user_ids ??
      item?.assignees ??
      item?.assignedTo ??
      item?.assigned_to ??
      item?.assignee ??
      item?.assignee_id,
  );

export const resolveEffectiveSuggestedAssignee = ({
  suggestedAssignee,
  assignedTo,
  fallbackSuggestedAssignee = null,
} = {}) => {
  const normalizedSuggestedAssignee = coerceNullableString(suggestedAssignee);

  if (normalizedSuggestedAssignee) {
    return normalizedSuggestedAssignee;
  }

  if (getActionItemAssignedToValues({ assignedTo }).length > 0) {
    return null;
  }

  return coerceNullableString(fallbackSuggestedAssignee);
};

export const getActionItemStatusLabel = (status) =>
  ACTION_ITEM_STATUS_LABELS[normalizeActionItemStatus(status, false)] || "Open";

export const matchesActionItemStatusFilter = (
  itemOrStatus,
  filter = "all",
) => {
  const normalizedFilter = coerceString(filter).toLowerCase();

  if (
    !normalizedFilter ||
    normalizedFilter === "all" ||
    normalizedFilter === "all statuses"
  ) {
    return true;
  }

  const normalizedStatus =
    itemOrStatus && typeof itemOrStatus === "object"
      ? normalizeActionItemStatus(itemOrStatus.status, itemOrStatus.completed)
      : normalizeActionItemStatus(itemOrStatus, false);

  const filterStatus = normalizeActionItemStatus(normalizedFilter, false);
  return normalizedStatus === filterStatus;
};

export const collectActionItemPersonIdentifiers = (items = []) =>
  items.reduce((identifiers, item) => {
    if (!item || typeof item !== "object") {
      return identifiers;
    }

    getActionItemAssignedToValues(item).forEach((candidate) => {
      const normalizedCandidate = coerceNullableString(candidate);
      if (normalizedCandidate) {
        identifiers.push(normalizedCandidate);
      }
    });

    [item?.suggestedAssignee, item?.suggested_assignee, item?.reviewer].forEach(
      (candidate) => {
        const normalizedCandidate = coerceNullableString(candidate);
        if (normalizedCandidate) {
          identifiers.push(normalizedCandidate);
        }
      },
    );

    return identifiers;
  }, []);

export const resolveActionItemPersonOption = (value, assigneeOptions = []) => {
  const normalizedValue = normalizeActionItemPersonId(value);
  if (!normalizedValue) {
    return null;
  }

  return (
    assigneeOptions.find((option) => {
      const optionUserId = coerceString(option?.userId);
      const optionEmail = coerceString(option?.email).toLowerCase();
      return (
        optionUserId === normalizedValue ||
        (optionEmail && optionEmail === normalizedValue.toLowerCase())
      );
    }) || null
  );
};

export const resolveActionItemPersonLabel = ({
  value,
  assigneeOptions = [],
  fallback = "",
} = {}) => {
  const normalizedValue = normalizeActionItemPersonId(value);
  if (!normalizedValue) {
    return fallback;
  }

  const option = resolveActionItemPersonOption(normalizedValue, assigneeOptions);
  if (coerceString(option?.label)) {
    return coerceString(option.label);
  }

  if (coerceString(option?.email)) {
    return coerceString(option.email);
  }

  return isEmailLike(normalizedValue) ? normalizedValue : fallback;
};

// Card/list label for an assignee or reviewer. Unresolved roster options carry
// the raw id as their label, so only a resolved option (or an email) counts as
// a name; anything else reads as ACTION_ITEM_UNKNOWN_PERSON_LABEL.
export const getActionItemPersonDisplayLabel = ({
  value,
  assigneeOptions = [],
  emptyLabel = "Unassigned",
} = {}) => {
  const normalizedValue = normalizeActionItemPersonId(value);
  if (!normalizedValue) {
    return emptyLabel;
  }

  const option = resolveActionItemPersonOption(normalizedValue, assigneeOptions);
  const optionLabel = coerceString(option?.label);
  const isRawIdLabel =
    !optionLabel ||
    option?.resolved === false ||
    optionLabel === normalizedValue ||
    optionLabel === coerceString(option?.userId);

  if (option && !isRawIdLabel) {
    return optionLabel;
  }

  if (coerceString(option?.email) && option?.resolved !== false) {
    return coerceString(option.email);
  }

  return isEmailLike(normalizedValue) ? normalizedValue : ACTION_ITEM_UNKNOWN_PERSON_LABEL;
};

export const resolveActionItemCompletedAt = ({
  nextCompleted,
  existingCompletedAt = null,
  now = new Date().toISOString(),
} = {}) => {
  if (!nextCompleted) {
    return null;
  }

  const preserved = coerceNullableString(existingCompletedAt);
  return preserved || now;
};

const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeNullableDateTime = (value) => {
  if (!hasValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }

  const raw = coerceString(value);
  if (!raw) {
    return null;
  }

  // Preserve date-only strings (YYYY-MM-DD) without converting to a full ISO timestamp,
  // since adding midnight UTC would lose the intended date-only semantics.
  if (ISO_DATE_ONLY_PATTERN.test(raw)) {
    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  return parsed.toISOString();
};

// Like normalizeNullableDateTime but always returns a YYYY-MM-DD date string,
// extracting the date portion from full ISO timestamps. Returns null for invalid values.
export const normalizeActionItemDateOnly = (value) => {
  if (!hasValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }

  const raw = coerceString(value);
  if (!raw) {
    return null;
  }

  if (ISO_DATE_ONLY_PATTERN.test(raw)) {
    return raw;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10);
};

export const formatActionItemDueDate = (value) => {
  const dateOnly = normalizeActionItemDateOnly(value);
  if (!dateOnly) {
    return "No due date";
  }

  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const normalizeBoolean = (value) => value === true;

const normalizeNullableNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampMinutes = (value) => {
  if (value === null) {
    return null;
  }

  if (value < ACTION_ITEM_MINUTES_MIN) {
    return ACTION_ITEM_MINUTES_MIN;
  }

  if (value > ACTION_ITEM_MINUTES_MAX) {
    return ACTION_ITEM_MINUTES_MAX;
  }

  return value;
};

const normalizeEstimatedMinutes = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = normalizeNullableNumber(value);
  if (parsed === null) {
    return null;
  }

  const rounded = Math.round(parsed);
  return rounded > 0 ? clampMinutes(rounded) : null;
};

const normalizeActualMinutes = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = normalizeNullableNumber(value);
  if (parsed === null) {
    return null;
  }

  const rounded = Math.round(parsed);
  return rounded > 0 ? clampMinutes(rounded) : null;
};

export const formatActionItemEstimatedMinutes = (estimatedMinutes) => {
  const minutes = normalizeEstimatedMinutes(estimatedMinutes);
  if (minutes === null) {
    return null;
  }

  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (remainder === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainder}m`;
};

export const formatActionItemEstimatedMinutesA11y = (estimatedMinutes) => {
  const minutes = normalizeEstimatedMinutes(estimatedMinutes);
  if (minutes === null) {
    return null;
  }

  if (minutes < 60) {
    return `Estimated time ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const hoursLabel = `${hours} hour${hours === 1 ? "" : "s"}`;

  if (remainder === 0) {
    return `Estimated time ${hoursLabel}`;
  }

  return `Estimated time ${hoursLabel} ${remainder} minute${remainder === 1 ? "" : "s"}`;
};

const toTimestamp = (value, fallback = 0) => {
  if (!hasValue(value)) {
    return fallback;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  return parsed.getTime();
};

export const normalizeActionItemPriority = (value, fallback = undefined) => {
  if (typeof value !== "string") {
    return fallback;
  }

  const trimmed = value.trim();
  return ACTION_ITEM_PRIORITY_OPTIONS.includes(trimmed) ? trimmed : fallback;
};

export const normalizeActionItemStatus = (
  value,
  completed = false,
  fallback = "open",
) => {
  // Preserve Django's legacy read normalization rule: historical
  // completed=true records are completed even if they stored status=closed.
  if (completed) {
    return "completed";
  }

  if (typeof value !== "string") {
    return fallback;
  }

  const normalized = value.trim().toLowerCase();

  if (ACTION_ITEM_STATUS_OPTIONS.includes(normalized)) {
    return normalized;
  }

  return ACTION_ITEM_STATUS_ALIASES[normalized] || fallback;
};

export const isActionItemCompletedStatus = (status) =>
  normalizeActionItemStatus(status, false) === "completed";

// The one place that decides whether a task counts as done. Every checkbox and
// every completion toggle must go through this - surfaces that tested the raw
// `completed` flag disagreed with surfaces that tested the status string.
export const isActionItemCompleted = (item) =>
  normalizeActionItemStatus(item?.status, Boolean(item?.completed)) === "completed";

export const isActionItemTerminalStatus = (status) =>
  ACTION_ITEM_TERMINAL_STATUSES.includes(normalizeActionItemStatus(status, false));

export const isActionItemOpenStatus = (status) =>
  !isActionItemTerminalStatus(status);

// Task boards show four lanes. Every terminal status other than completed
// (closed_irrelevant, closed_outdated, closed, cancelled) shares the Closed
// lane; the card keeps its specific status label.
export const ACTION_ITEM_BOARD_COLUMN_KEYS = ["open", "in_progress", "completed", "closed"];

export const getActionItemBoardColumnKey = (itemOrStatus) => {
  const status =
    itemOrStatus && typeof itemOrStatus === "object"
      ? normalizeActionItemStatus(itemOrStatus.status, itemOrStatus.completed === true)
      : normalizeActionItemStatus(itemOrStatus, false);

  if (status === "completed" || status === "in_progress") {
    return status;
  }

  return isActionItemTerminalStatus(status) ? "closed" : "open";
};

export const normalizeActionItemClosureCategory = (value, status = null) => {
  const normalizedValue = coerceNullableString(value);

  if (
    normalizedValue &&
    ACTION_ITEM_CLOSURE_CATEGORY_OPTIONS.includes(normalizedValue)
  ) {
    return normalizedValue;
  }

  return ACTION_ITEM_DEFAULT_CLOSURE_CATEGORY_BY_STATUS[
    normalizeActionItemStatus(status, false)
  ] || null;
};

export const getActionItemDisplayTitle = (item) => {
  const title = coerceString(item?.title);
  if (title) {
    return title;
  }

  const description = coerceString(item?.description);
  if (description) {
    return description;
  }

  return "Untitled action item";
};

export const getActionItemDisplayDescription = (item) => {
  const description = coerceString(item?.description);
  const title = coerceString(item?.title);

  if (description && description !== title) {
    return description;
  }

  return "";
};

export const getActionItemCaseLabel = (item = {}) => {
  const candidates = [
    item?.caseTitle,
    item?.caseDetails?.caseTitle,
    item?.caseName,
    item?.caseDetails?.caseName,
    item?.caseId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCaseLabel(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "Case";
};

export const normalizeActionItemRecord = (item = {}) => {
  const sourceItem = item && typeof item === "object" ? item : {};
  const description = coerceString(sourceItem.description);
  const title = coerceString(sourceItem.title) || description;
  const rawCompleted = normalizeBoolean(sourceItem.completed);
  const priority = normalizeActionItemPriority(sourceItem.priority, "Mid");
  const completedAt = sourceItem.completedAt ?? sourceItem.completed_at ?? null;
  const closedAt = normalizeNullableDateTime(sourceItem.closedAt ?? sourceItem.closed_at ?? null);

  const itemId = coerceNullableString(sourceItem.itemId ?? sourceItem.item_id ?? sourceItem.id);
  const caseId = coerceNullableString(sourceItem.caseId ?? sourceItem.case_id);
  const userId = coerceNullableString(sourceItem.userId ?? sourceItem.user_id);
  const appId = coerceNullableString(sourceItem.appId ?? sourceItem.app_id);
  const assignedToValues = getActionItemAssignedToValues(sourceItem);
  const assignedTo = assignedToValues;
  const assignee = assignedToValues;
  const primaryAssignedTo = assignedToValues[0] ?? null;
  const suggestedAssignee = coerceNullableString(
    sourceItem.suggestedAssignee ?? sourceItem.suggested_assignee,
  );
  const suggestedDueDate = normalizeActionItemDateOnly(
    sourceItem.suggestedDueDate ??
      sourceItem.suggested_due_date ??
      sourceItem.dueDate ??
      sourceItem.due_date ??
      sourceItem.date ??
      null,
  );
  const estimatedMinutes = normalizeEstimatedMinutes(
    sourceItem.estimatedMinutes ?? sourceItem.estimated_minutes,
  );
  const actualMinutes = normalizeActualMinutes(
    sourceItem.actualMinutes ?? sourceItem.actual_minutes,
  );
  const docketEntryId = coerceNullableString(
    sourceItem.docketEntryId ?? sourceItem.docket_entry_id,
  );
  const entryNumber = coerceNullableString(
    sourceItem.entryNumber ?? sourceItem.entry_number,
  );
  const assistantRunId = coerceNullableString(
    sourceItem.assistantRunId ?? sourceItem.assistant_run_id,
  );
  const assistantExcerpt = coerceNullableString(
    sourceItem.assistantExcerpt ?? sourceItem.assistant_excerpt,
  );
  const contextHash = coerceNullableString(
    sourceItem.contextHash ?? sourceItem.context_hash,
  );
  const lastUpdatedBy = coerceNullableString(
    sourceItem.lastUpdatedBy ?? sourceItem.last_updated_by,
  );
  const lastUpdatedSource = coerceNullableString(
    sourceItem.lastUpdatedSource ?? sourceItem.last_updated_source,
  );
  const completionReason = coerceNullableString(
    sourceItem.completionReason ?? sourceItem.completion_reason,
  );
  const reviewer = normalizeActionItemPersonId(sourceItem.reviewer);
  // Atticus normalises record timestamps to ISO-8601 (its Overview compares
  // and sorts them); LitigAI, SeriesAI, in-house and home pass them through.
  // The host declares which contract it wants via the product descriptor
  // (features.actionItemIsoTimestamps) so this module carries no appId logic.
  const isoTimestamps = hasFeature("actionItemIsoTimestamps");
  const rawCreatedAt = sourceItem.createdAt ?? sourceItem.created_at ?? null;
  const rawUpdatedAt = sourceItem.updatedAt ?? sourceItem.updated_at ?? rawCreatedAt ?? null;
  const createdAt = isoTimestamps ? normalizeNullableDateTime(rawCreatedAt) : rawCreatedAt;
  const updatedAt = isoTimestamps ? normalizeNullableDateTime(rawUpdatedAt) : rawUpdatedAt;
  const status = normalizeActionItemStatus(sourceItem.status, rawCompleted);
  const completed = status === "completed";
  const closureCategory = normalizeActionItemClosureCategory(
    sourceItem.closureCategory ?? sourceItem.closure_category,
    status,
  );

  return {
    ...sourceItem,
    itemId,
    caseId,
    userId,
    appId,
    title,
    description,
    estimatedMinutes,
    actualMinutes,
    priority,
    assignedTo,
    assignee,
    primaryAssignedTo,
    assignedToValues,
    assignees: assignedToValues,
    userIds: assignedToValues,
    suggestedAssignee,
    suggestedDueDate,
    docketEntryId,
    entryNumber,
    status,
    completed,
    completedAt: completed ? completedAt : null,
    closedAt: isActionItemTerminalStatus(status) && status !== "completed" ? closedAt : null,
    closureCategory,
    closureReason: isActionItemOpenStatus(status)
      ? null
      : coerceNullableString(sourceItem.closureReason ?? sourceItem.closure_reason),
    closureReasonCode: isActionItemOpenStatus(status)
      ? null
      : coerceNullableString(
          sourceItem.closureReasonCode ?? sourceItem.closure_reason_code,
        ),
    source: coerceNullableString(sourceItem.source),
    origin: coerceNullableString(sourceItem.origin),
    signature: coerceNullableString(sourceItem.signature),
    rawEntry: sourceItem.rawEntry ?? null,
    assistantRunId,
    assistantExcerpt,
    contextHash,
    lastUpdatedBy,
    lastUpdatedSource,
    completionReason,
    reviewer,
    createdAt,
    updatedAt,
    rank: normalizeNullableNumber(sourceItem.rank),
    date: isoTimestamps ? normalizeNullableDateTime(sourceItem.date ?? null) : sourceItem.date ?? null,
  };
};

export const sortActionItems = (items = []) =>
  [...items].sort((left, right) => {
    const a = normalizeActionItemRecord(left);
    const b = normalizeActionItemRecord(right);

    const priorityDiff =
      (ACTION_ITEM_PRIORITY_WEIGHT[b.priority] || 0) -
      (ACTION_ITEM_PRIORITY_WEIGHT[a.priority] || 0);

    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const dueDiff =
      toTimestamp(a.suggestedDueDate, Number.MAX_SAFE_INTEGER) -
      toTimestamp(b.suggestedDueDate, Number.MAX_SAFE_INTEGER);

    if (dueDiff !== 0) {
      return dueDiff;
    }

    return (
      toTimestamp(b.updatedAt || b.createdAt) -
      toTimestamp(a.updatedAt || a.createdAt)
    );
  });

export const buildActionItemInput = (input = {}, defaults = {}, options = {}) => {
  const inputObject = input && typeof input === "object" ? input : {};
  const merged = { ...defaults, ...inputObject };
  const normalizedTitle =
    coerceString(merged.title) || coerceString(merged.description);
  const normalizedDescription = coerceString(merged.description);
  const normalizedStatus = normalizeActionItemStatus(
    merged.status,
    normalizeBoolean(merged.completed) && !merged.status,
  );
  const completed = normalizedStatus === "completed";
  const isOpenStatus = isActionItemOpenStatus(normalizedStatus);
  const normalizedPriority = normalizeActionItemPriority(merged.priority, "Mid");
  const hasSuggestedDueDate = Object.prototype.hasOwnProperty.call(merged, "suggestedDueDate");
  const suggestedDueDateValue = hasSuggestedDueDate ? merged.suggestedDueDate : merged.date;
  const normalizedSuggestedDueDate = normalizeActionItemDateOnly(suggestedDueDateValue ?? null);
  const inputHasSuggestedDueDate = Object.prototype.hasOwnProperty.call(inputObject, "suggestedDueDate");
  const inputDueDateValue = inputHasSuggestedDueDate ? inputObject.suggestedDueDate : inputObject.date;
  const dueDateWasExplicitlyCleared =
    options.forUpdate === true &&
    (inputHasSuggestedDueDate || Object.prototype.hasOwnProperty.call(inputObject, "date")) &&
    inputDueDateValue == null;
  const normalizedCompletedAt = Object.prototype.hasOwnProperty.call(merged, "completedAt")
    ? merged.completedAt
    : Object.prototype.hasOwnProperty.call(merged, "completed_at")
      ? merged.completed_at
      : undefined;
  // The backend drops null minute fields (indistinguishable from omitted), so
  // an explicit clear must ride as the -1 sentinel the backend removes on.
  const normalizedEstimatedMinutes =
    merged.estimatedMinutes === undefined
      ? undefined
      : normalizeEstimatedMinutes(merged.estimatedMinutes) ?? -1;
  const normalizedActualMinutes =
    merged.actualMinutes === undefined
      ? undefined
      : normalizeActualMinutes(merged.actualMinutes) ?? -1;
  const hasClosureCategory =
    merged.closureCategory !== undefined ||
    merged.closure_category !== undefined;
  const hasClosureReason =
    merged.closureReason !== undefined ||
    merged.closure_reason !== undefined;
  const hasClosureReasonCode =
    merged.closureReasonCode !== undefined ||
    merged.closure_reason_code !== undefined;
  const normalizedAssignees = getActionItemAssignedToValues(merged);
  const payload = Object.fromEntries(
    Object.entries({
      itemId: coerceNullableString(merged.itemId) ?? undefined,
      caseId: coerceNullableString(merged.caseId) ?? undefined,
      userId: coerceNullableString(merged.userId) ?? undefined,
      appId: coerceNullableString(merged.appId) ?? undefined,
      title: normalizedTitle,
      description: normalizedDescription,
      estimatedMinutes: normalizedEstimatedMinutes,
      actualMinutes: normalizedActualMinutes,
      priority: normalizedPriority,
      assignedTo: normalizedAssignees,
      assignee: normalizedAssignees,
      suggestedAssignee: coerceNullableString(merged.suggestedAssignee),
      suggestedDueDate: normalizedSuggestedDueDate,
      clearSuggestedDueDate: dueDateWasExplicitlyCleared || undefined,
      docketEntryId: coerceNullableString(merged.docketEntryId),
      entryNumber: coerceNullableString(merged.entryNumber),
      status: normalizedStatus,
      completed,
      closureCategory: isOpenStatus
        ? null
        : hasClosureCategory
          ? normalizeActionItemClosureCategory(
              merged.closureCategory ?? merged.closure_category,
              normalizedStatus,
            )
          : undefined,
      closureReason: isOpenStatus
        ? null
        : hasClosureReason
          ? coerceNullableString(merged.closureReason ?? merged.closure_reason)
          : undefined,
      closureReasonCode: isOpenStatus
        ? null
        : hasClosureReasonCode
          ? coerceNullableString(merged.closureReasonCode ?? merged.closure_reason_code)
          : undefined,
      source: coerceNullableString(merged.source),
      origin: coerceNullableString(merged.origin),
      signature: coerceNullableString(merged.signature),
      rawEntry: merged.rawEntry,
      assistantRunId: coerceNullableString(merged.assistantRunId),
      assistantExcerpt: coerceNullableString(merged.assistantExcerpt),
      contextHash: coerceNullableString(merged.contextHash),
      lastUpdatedBy: coerceNullableString(merged.lastUpdatedBy),
      lastUpdatedSource: coerceNullableString(merged.lastUpdatedSource),
      completionReason: coerceNullableString(merged.completionReason),
      createdAt: normalizeNullableDateTime(merged.createdAt) ?? undefined,
      updatedAt: normalizeNullableDateTime(merged.updatedAt) ?? undefined,
      startDate: normalizeNullableDateTime(merged.startDate) ?? undefined,
      completedAt: normalizeNullableDateTime(normalizedCompletedAt) ?? undefined,
      closedAt:
        merged.closedAt === undefined && merged.closed_at === undefined
          ? undefined
          : normalizeNullableDateTime(merged.closedAt ?? merged.closed_at),
      archivedAt: normalizeNullableDateTime(merged.archivedAt) ?? undefined,
      date: normalizeNullableDateTime(merged.date) ?? undefined,
      parentTaskId: coerceNullableString(merged.parentTaskId),
      sortOrder: normalizeNullableNumber(merged.sortOrder),
      // The GraphQL backend cannot tell an explicit reviewer: null apart from
      // an omitted field (both arrive as None), so a null clear silently
      // no-ops server-side. An empty string is the only value that persists a
      // reviewer clear, mirroring the suggestedAssignee convention.
      reviewer: Object.prototype.hasOwnProperty.call(merged, "reviewer")
        ? normalizeActionItemPersonId(merged.reviewer) ?? ""
        : undefined,
      // Dismissing an agent closure proposal. Same trap as reviewer above: the
      // backend cannot tell `null` from an omitted field, so a clear has to
      // ride as "". Sending the key at all is what the backend reads as an
      // explicit dismiss, which also drops the pending reply token.
      suggestedClosureStatus: Object.prototype.hasOwnProperty.call(merged, "suggestedClosureStatus")
        ? coerceNullableString(merged.suggestedClosureStatus) ?? ""
        : undefined,
      labels: normalizeStringSet(merged.labels),
      watcherIds: normalizeStringSet(merged.watcherIds),
      linkedPeople: normalizeStringSet(merged.linkedPeople),
      linkedFilings: normalizeStringSet(merged.linkedFilings),
      memoHtml: Object.prototype.hasOwnProperty.call(merged, "memoHtml")
        ? coerceNullableString(merged.memoHtml)
        : undefined,
      rank: coerceNullableString(merged.rank),
    }).filter(([, value]) => value !== undefined),
  );

  if (options.forUpdate) {
    const inputKeys = new Set(Object.keys(input || {}));
    const supplied = (...keys) => keys.some((key) => inputKeys.has(key));
    const sourceKeysByPayloadKey = {
      itemId: ["itemId"],
      caseId: ["caseId"],
      userId: ["userId"],
      appId: ["appId"],
      title: ["title", "description"],
      description: ["title", "description"],
      estimatedMinutes: ["estimatedMinutes", "estimated_minutes"],
      actualMinutes: ["actualMinutes", "actual_minutes"],
      priority: ["priority"],
      assignedTo: ["assignedTo", "assigned_to", "assignedToValues", "assigned_to_values", "assigneeValues", "assignee_values", "assignees", "assignee", "assignee_id", "userIds", "user_ids"],
      assignee: ["assignedTo", "assigned_to", "assignedToValues", "assigned_to_values", "assigneeValues", "assignee_values", "assignees", "assignee", "assignee_id", "userIds", "user_ids"],
      suggestedAssignee: ["suggestedAssignee", "suggested_assignee"],
      suggestedDueDate: ["suggestedDueDate", "suggested_due_date", "dueDate", "due_date", "date"],
      clearSuggestedDueDate: ["suggestedDueDate", "suggested_due_date", "dueDate", "due_date", "date"],
      docketEntryId: ["docketEntryId", "docket_entry_id"],
      entryNumber: ["entryNumber", "entry_number"],
      status: ["status", "completed"],
      completed: ["status", "completed"],
      closureCategory: ["closureCategory", "closure_category"],
      closureReason: ["closureReason", "closure_reason"],
      closureReasonCode: ["closureReasonCode", "closure_reason_code"],
      source: ["source"],
      origin: ["origin"],
      signature: ["signature"],
      rawEntry: ["rawEntry", "raw_entry"],
      assistantRunId: ["assistantRunId", "assistant_run_id"],
      assistantExcerpt: ["assistantExcerpt", "assistant_excerpt"],
      contextHash: ["contextHash", "context_hash"],
      lastUpdatedBy: ["lastUpdatedBy", "last_updated_by"],
      lastUpdatedSource: ["lastUpdatedSource", "last_updated_source"],
      completionReason: ["completionReason", "completion_reason"],
      createdAt: ["createdAt", "created_at"],
      updatedAt: ["updatedAt", "updated_at"],
      startDate: ["startDate", "start_date"],
      completedAt: ["completedAt", "completed_at"],
      closedAt: ["closedAt", "closed_at"],
      archivedAt: ["archivedAt", "archived_at"],
      date: ["date"],
      parentTaskId: ["parentTaskId", "parent_task_id"],
      sortOrder: ["sortOrder", "sort_order"],
      rank: ["rank"],
      reviewer: ["reviewer"],
      suggestedClosureStatus: ["suggestedClosureStatus", "suggested_closure_status"],
      labels: ["labels"],
      watcherIds: ["watcherIds", "watcher_ids"],
      linkedPeople: ["linkedPeople", "linked_people"],
      linkedFilings: ["linkedFilings", "linked_filings"],
      memoHtml: ["memoHtml", "memo_html"],
    };

    Object.entries(sourceKeysByPayloadKey).forEach(([payloadKey, sourceKeys]) => {
      if (!supplied(...sourceKeys)) delete payload[payloadKey];
    });
  }

  return payload;
};

// Ported from Atticus (the only fork that had them): open-list filtering and
// input normalisation on top of the shared status vocabulary.
export const filterOpenActionItems = (items = []) =>
  items.filter((item) =>
    isActionItemOpenStatus(normalizeActionItemStatus(item?.status, item?.completed === true)),
  );

export const normalizeActionItemInput = (input = {}) => {
  const status = normalizeActionItemStatus(input?.status, input?.completed === true);
  const completed = status === "completed";
  return { ...input, status, completed };
};
